// #region Imports
import SaveIcon from "@mui/icons-material/Save";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { getApiErrorMessage, isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import LoadingButton from "_shared/_views/loading/loading-button";
import TagInput from "_shared/_views/tags/tag-input";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import EditFeeScheduleCategory from "_shared/fee-schedules/categories/_views/edit-fee-schedule-category";
import FeeScheduleCategoryList from "_shared/fee-schedules/categories/_views/fee-schedule-category-list";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import {
  useCreateFeeScheduleMutation,
  useUpdateFeeScheduleMutation,
} from "marketplace/teams/fee-schedules/_api/fee-schedule-api";
import UpdateFeeScheduleCommand from "marketplace/teams/fee-schedules/_api/commands/update-fee-schedule-command";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import ExistingFeeScheduleTemplatePicker from "work/fee-schedule-templates/_views/lists/existing-fee-schedule-template-picker";
import type { TemplateCategoriesApplied } from "work/fee-schedule-templates/_views/lists/fee-schedule-template-category-picker";
// The one editable fee schedule categories table in the app — see the note in team-fee-schedules.tsx.
import FeeScheduleCategoriesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
// Reused for the dedupe rule rather than restated, so pulling categories in here means the same
// thing it does on the fee schedule template page: same name, already present, not added twice.
import WorkAgreementFeeSchedule from "work/work-agreements/fee-schedules/fee-schedule";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minHeight: 0,
  // Room above the first field for its floating label, which would otherwise be clipped by whatever
  // the form opens under — the schedule's own list entry, now that this is not a dialog.
  paddingTop: theme.spacing(3),
  width: "100%",
}));
const DetailsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(3),
  "& > *": {
    flex: 1,
    minWidth: theme.spacing(30),
  },
}));
const CategoriesTable = styled(FeeScheduleCategoriesTable)(() => ({
  maxWidth: "100%",
  minWidth: 0,
}));
/**
 * The category editor where it now lives: in the table, under the row it belongs to (issue #5111).
 *
 * Framed rather than merely inset, because a form drawn inside a table body is otherwise hard to
 * tell from the rows around it — the tint and the left rule say where the row ends and the form
 * that belongs to it begins.
 */
const InlineCategoryEditor = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderLeft: `3px solid ${theme.palette.primary.main}`,
  padding: theme.spacing(2),
}));
const NewCategoryEditor = styled(InlineCategoryEditor)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  justifyContent: "flex-end",
  paddingTop: theme.spacing(1),
}));
/** Beside the button as well as on it: a tooltip only helps someone already reaching for the save. */
const SaveValidationMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  textAlign: "right",
}));
// #endregion

/**
 * Module scope so the `??` chain stays out of the component's `catch` blocks — either one would
 * silently cost the whole component its React Compiler memoization.
 */
function describeSaveError(error: unknown, action: "create" | "update"): string {
  if (isFetchBaseQueryError(error)) {
    const apiMessage = getApiErrorMessage(error.data, {
      includeFallbackStrings: false,
      prioritizedFields: ["name", "tags", "categories"],
    });
    if (apiMessage) return apiMessage;
  }

  return action === "create"
    ? "Couldn't save fee schedule. Please try again."
    : "Couldn't save fee schedule changes. Please try again.";
}

function noop(): void {
  // The default for an omitted callback, so the component never optional-chains one in a `try`.
}

// #region Component Input Props
export type EditTeamFeeScheduleProps = {
  className?: string;
  /** Omitted when creating: the form then saves a new schedule instead of patching one. */
  feeSchedule?: FeeSchedule;
  /** The members a category's rate can be assigned to. The API rejects anyone outside the team. */
  teamMembers: readonly EntityMember[];
  teamId: Guid;
  onSaved?: (feeSchedule: FeeSchedule) => void;
};
// #endregion

/** Creates or edits one of a team's marketplace fee schedules: its name, its tags, and its rates. */
export default function EditTeamFeeSchedule(props: Readonly<EditTeamFeeScheduleProps>) {
  const { className, feeSchedule, teamId, teamMembers } = props;
  const onSaved = props.onSaved ?? noop;

  // #region State
  const [name, setName] = useState<string>(feeSchedule?.name ?? "");
  const [tags, setTags] = useState<FeeScheduleTag[]>(feeSchedule?.tags ?? []);
  const [categories, setCategories] = useState<FeeScheduleCategory[]>(feeSchedule?.categories ?? []);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  /**
   * Which category editor is open, if any: the id of a category being edited, or "new".
   *
   * One at a time. The editor is drawn inside the table, so two open at once would put two forms
   * between rows with nothing to say which row either belonged to.
   */
  const [openCategoryEditor, setOpenCategoryEditor] = useState<string | undefined>(undefined);
  // #endregion

  // #region Hooks
  const { openDialog, popDialog } = useDialog();
  const [createFeeSchedule, { isLoading: isCreating }] = useCreateFeeScheduleMutation();
  const [updateFeeSchedule, { isLoading: isUpdating }] = useUpdateFeeScheduleMutation();
  // #endregion

  const isSaving = isCreating || isUpdating;
  const trimmedName = name.trim();
  const currentFeeSchedule = feeSchedule
    ? new FeeSchedule({ id: feeSchedule.id, name: trimmedName, tags, categories })
    : undefined;
  const hasScheduleChanges =
    !feeSchedule ||
    (currentFeeSchedule !== undefined && new UpdateFeeScheduleCommand(feeSchedule, currentFeeSchedule).payload.length > 0);

  /**
   * An untouched form says nothing, the same way the profile form it opens from does: save stays
   * disabled until something changes, but that is the state the editor opens in, and naming it
   * reads as an error against work nobody has started.
   */
  function saveValidationMessage(): string {
    if (!trimmedName) return "Give the fee schedule a name.";
    if (categories.length < 1) return "Add at least one fee category.";
    if (deferredCategories.length > 0) {
      const names = deferredCategories.map((category) => category.name.value).join(", ");
      return `Set a rate for every category: ${names}.`;
    }
    if (unstaffedHourlyCategories.length > 0) {
      const names = unstaffedHourlyCategories.map((category) => category.name.value).join(", ");
      return `Assign a team member to every hourly rate: ${names}.`;
    }
    return "";
  }

  /**
   * Hourly rates that name nobody.
   *
   * An hourly fee has to say who is billing it (issue #4489); an hourly expense does not (#6883). And the category editor makes the
   * field required one row at a time. Categories pulled from the catalog never pass through that
   * editor and carry no member — a catalog row is shared by every template in the firm that names
   * it, so it has no person of its own — so the rule is checked for the schedule as a whole as
   * well. A template's categories do name people since #5237, but this team's roster is not the
   * roster they were named from, so they are checked here like any other.
   */
  const unstaffedHourlyCategories = categories.filter(
    (category) => category.hourlyRateNeedsBiller && !category.teamMember,
  );

  /**
   * Categories with no fee at all.
   *
   * A category may defer its fee to the other side, and in a work agreement or a proposal that is a
   * real state. Published marketplace rates have no other side, and the API contract cannot carry
   * the state anyway: `FeeScheduleCategoryCommand.FeeRate` is a plain `decimal`, so a category sent
   * without a rate binds to `0` and comes back a $0 fixed fee — rates the team never set, on their
   * public profile. The category editor below is opened with deferral turned off, but categories
   * pulled from the catalog or from a template never pass through it, so the schedule is checked
   * as a whole as well.
   */
  const deferredCategories = categories.filter((category) => !category.fee);

  const validationMessage = saveValidationMessage();

  function handleCategoryAdded(category: FeeScheduleCategory) {
    setCategories((previousCategories) => [...previousCategories, category]);
    setIsFormDirty(true);
    setOpenCategoryEditor(undefined);
  }

  /**
   * Matched on id rather than on the name the editor hands back, so renaming a category still lands
   * on the row being edited. The editor preserves the id of the category it was opened with.
   */
  function handleCategoryUpdated(updated: FeeScheduleCategory) {
    setCategories((previousCategories) =>
      previousCategories.map((category) => (category.id.isEqualTo(updated.id) ? updated : category))
    );
    setIsFormDirty(true);
    setOpenCategoryEditor(undefined);
  }

  function handleCategoryRemoved(removedName: FeeScheduleCategoryName) {
    // The editor is anchored to a row, so it goes when the row does - otherwise it would be left
    // open under whichever category slid up into that position.
    const removed = categories.find((category) => category.name.isEqualTo(removedName));
    if (removed && openCategoryEditor === removed.id.value) setOpenCategoryEditor(undefined);
    setCategories((previousCategories) =>
      previousCategories.filter((category) => !category.name.isEqualTo(removedName))
    );
    setIsFormDirty(true);
  }

  /**
   * Adds only the categories the schedule does not already hold, and says so when it skipped some.
   * Same rule as the fee schedule template page: a category already there by name is not added
   * twice, and the ones that are new are kept rather than the whole selection refused.
   */
  function addCategories(incoming: readonly FeeScheduleCategory[]): void {
    const { duplicateCategories, uniqueCategories } = WorkAgreementFeeSchedule.splitCategoriesByDuplicate(
      [...categories],
      [...incoming],
    );
    const added = uniqueCategories.filter(
      (category): category is FeeScheduleCategory => category instanceof FeeScheduleCategory,
    );

    if (added.length > 0) {
      setCategories((previousCategories) => [...previousCategories, ...added]);
      setIsFormDirty(true);
    }

    if (duplicateCategories.length > 0) {
      enqueueSnackbar(
        `Skipped ${duplicateCategories.length} categor${duplicateCategories.length === 1 ? "y" : "ies"} already in this fee schedule.`,
        { variant: "info" },
      );
    }
  }

  /** The firm's own catalog of categories, the same list the fee schedule template page offers. */
  function openExistingCategoryDialog(): void {
    openDialog({
      title: "Select Existing Categories",
      MuiProps: { maxWidth: "sm", fullWidth: true },
      component: (
        <FeeScheduleCategoryList
          disabledCategories={categories}
          onCancel={popDialog}
          onCategoriesSelected={(selected) => {
            addCategories(selected);
            popDialog();
          }}
        />
      ),
    });
  }

  /**
   * The categories the reader took from a template, replacing what is here or added to it (#6604).
   *
   * The reader chose which, and which categories, in the dialog's footer while looking at them. A team
   * edits its own rates, so a masked category is never among them and is dropped if it were.
   */
  function handleTemplateCategoriesApplied({ categories: chosen, mode }: TemplateCategoriesApplied): void {
    const incoming = chosen.filter(
      (category): category is FeeScheduleCategory => category instanceof FeeScheduleCategory,
    );

    if (mode === "replace" || categories.length < 1) {
      setCategories(incoming);
      setIsFormDirty(true);
    } else {
      addCategories(incoming);
    }
    popDialog();
  }

  function openExistingTemplateDialog(): void {
    openDialog({
      title: "Select Existing Template",
      MuiProps: { maxWidth: "sm", fullWidth: true },
      component: (
        <ExistingFeeScheduleTemplatePicker
          existingCategories={categories}
          onCancel={popDialog}
          onApply={handleTemplateCategoriesApplied}
        />
      ),
    });
  }

  /**
   * `EditFeeScheduleCategory` hands back either a plain or a masked-currency category. A team edits
   * its own rates, so nothing is masked here and anything else is dropped rather than half-saved.
   */
  function renderCategoryForm(category?: FeeScheduleCategory) {
    return (
      <EditFeeScheduleCategory
        category={category}
        selectedCategories={categories}
        // The team's roster is the whole of the answer here: the API rejects anyone outside it, so this
        // is the one surface that keeps a list rather than the picker.
        offersTeamMember
        teamMembers={teamMembers}
        // A published rate is billed by somebody on this team, so an hourly one says who (#4489).
        hourlyRateNamesItsBiller
        // Nobody to defer a published rate to, and the API would store the deferral as $0.
        allowDeferredFee={false}
        onCancel={() => setOpenCategoryEditor(undefined)}
        onCategoryAdded={(added) => {
          if (added instanceof FeeScheduleCategory) handleCategoryAdded(added);
        }}
        onCategoryUpdated={(_originalName, updated) => {
          if (updated instanceof FeeScheduleCategory) handleCategoryUpdated(updated);
        }}
      />
    );
  }

  async function handleSaveClicked() {
    const updated = FeeSchedule.create({
      id: feeSchedule?.id,
      name: trimmedName,
      tags,
      categories,
    });

    if (!feeSchedule) {
      try {
        const created = await createFeeSchedule({ teamId: teamId.value, feeSchedule: updated }).unwrap();
        enqueueSnackbar("Created fee schedule", { variant: "success" });
        setIsFormDirty(false);
        onSaved(FeeSchedule.fromJSON(created));
      } catch (error) {
        console.error(error);
        enqueueSnackbar(describeSaveError(error, "create"), { variant: "error" });
      }
      return;
    }

    try {
      if (!hasScheduleChanges) {
        setIsFormDirty(false);
        return;
      }

      const saved = await updateFeeSchedule({
        teamId: teamId.value,
        scheduleId: feeSchedule.id.value,
        originalFeeSchedule: feeSchedule,
        updatedFeeSchedule: updated,
      }).unwrap();
      enqueueSnackbar("Updated fee schedule", { variant: "success" });
      setIsFormDirty(false);
      onSaved(FeeSchedule.fromJSON(saved));
    } catch (error) {
      console.error(error);
      enqueueSnackbar(describeSaveError(error, "update"), { variant: "error" });
    }
  }

  // #region Render
  return (
    /* Named as a group so the form can be addressed as a whole now that it is no longer a dialog:
       e2e scopes its assertions to this rather than to the page, which also holds the profile form
       it opens inside and, briefly, a schedule editor that is collapsing shut. */
    <MainContainer
      className={className}
      role="group"
      aria-label={feeSchedule ? `Edit fee schedule ${feeSchedule.name}` : "New fee schedule"}
    >
      <DetailsContainer>
        <DebouncedTextField
          required
          label="Name"
          helperText="Shown on your team's marketplace profile"
          slotProps={{ htmlInput: { maxLength: 50 } }}
          value={name}
          onDebouncedChange={(newValue: string) => {
            setName(newValue);
            setIsFormDirty(true);
          }}
        />
        <TagInput
          helperText={`Tags to help you find this fee schedule later (ex. US, ${new AHDate().year})`}
          tags={tags.map((tag) => tag.value)}
          onTagsUpdated={(tagValues: string[]) => {
            setTags(tagValues.map((tagValue) => new FeeScheduleTag(tagValue)));
            setIsFormDirty(true);
          }}
          onTagRemoved={(tagValue: string) => {
            setTags((previousTags) => previousTags.filter((previousTag) => previousTag.value !== tagValue));
            setIsFormDirty(true);
          }}
        />
      </DetailsContainer>
      <CategoriesTable
        selectedCategories={categories}
        hideFieldHistory
        // Both pull from the work domain's own catalogs, which a marketplace team has no grant to read.
        onExistingCategoryClicked={openExistingCategoryDialog}
        onExistingTemplateClicked={openExistingTemplateDialog}
        // "New" on the toolbar's split button opens the same inline form the row editors use, so
        // every way of adding a category starts from one control.
        onCreateCategoryClicked={() => setOpenCategoryEditor((open) => (open === "new" ? undefined : "new"))}
        editingCategoryId={openCategoryEditor}
        renderCategoryEditor={(category) => (
          <InlineCategoryEditor>
            {renderCategoryForm(category instanceof FeeScheduleCategory ? category : undefined)}
          </InlineCategoryEditor>
        )}
        onEditCategory={(category) => {
          if (!(category instanceof FeeScheduleCategory)) return;
          // A second press on the row that is already open closes it, so Edit is a toggle rather
          // than a control that only ever opens something.
          setOpenCategoryEditor((open) => (open === category.id.value ? undefined : category.id.value));
        }}
        onCategoryRemoved={handleCategoryRemoved}
      />
      <Collapse in={openCategoryEditor === "new"} unmountOnExit>
        <NewCategoryEditor>{renderCategoryForm()}</NewCategoryEditor>
      </Collapse>
      <ButtonContainer>
        {validationMessage && (
          <SaveValidationMessage variant="caption">{validationMessage}</SaveValidationMessage>
        )}
        <Tooltip title={validationMessage} placement="top">
          <span>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              loading={isSaving}
              disabled={!!validationMessage || isSaving || !isFormDirty || !hasScheduleChanges}
              onClick={handleSaveClicked}
            >
              {feeSchedule ? "Save Changes" : "Save"}
            </LoadingButton>
          </span>
        </Tooltip>
      </ButtonContainer>
    </MainContainer>
  );
  // #endregion
}
